"use client";

import { PaginationControls } from "@/components/ui/pagination";
import { ListSkeleton } from "@/components/shared";
import { PublisherGameFollowersList } from "./PublisherGameFollowersList";
import { useListPagination } from "@/hooks";
import type { PublisherGameFollowers } from "../types";
import type { PublisherGameFollowersSort } from "../api/getPublisherGameFollowers";
import { publisherGameFollowersSkeletonConfig } from "../config";

interface PublisherGameFollowersListWithPaginationProps {
  initialGames: PublisherGameFollowers[];
  initialPage: number;
  initialTotal: number;
  pageSize: number;
  clanid: number;
  initialSort: PublisherGameFollowersSort;
}

async function loadPublisherGameFollowers(
  clanid: number,
  page: number,
  pageSize: number,
  sort?: PublisherGameFollowersSort | null,
): Promise<{ items: PublisherGameFollowers[]; total: number }> {
  const { getPublisherGameFollowersClient } =
    await import("../api/getPublisherGameFollowersClient");
  const offset = (page - 1) * pageSize;
  const { games, total } = await getPublisherGameFollowersClient(
    clanid,
    pageSize,
    offset,
    sort || "followers:desc",
  );
  return { items: games, total };
}

export function PublisherGameFollowersListWithPagination({
  initialGames,
  initialPage,
  initialTotal,
  pageSize,
  clanid,
  initialSort,
}: PublisherGameFollowersListWithPaginationProps) {
  const {
    items: games,
    currentPage,
    currentSort,
    pageSize: currentPageSize,
    totalPages,
    isPending,
    handlePageChange,
    handleSortChange,
    handlePageSizeChange,
  } = useListPagination({
    initialItems: initialGames,
    initialPage,
    initialTotal,
    pageSize,
    sortable: true,
    initialSort,
    onPageChange: (page, pageSize, sort) =>
      loadPublisherGameFollowers(clanid, page, pageSize, sort),
  });

  return (
    <div className="space-y-8">
      {isPending ? (
        <ListSkeleton columns={publisherGameFollowersSkeletonConfig} />
      ) : (
        <PublisherGameFollowersList
          games={games}
          sortBy={currentSort!}
          onSortChange={handleSortChange!}
        />
      )}

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={currentPageSize}
        onPageSizeChange={handlePageSizeChange!}
        disabled={isPending}
      />
    </div>
  );
}
